import type { Position, PositionSide } from "./types";
import type { MarketSymbol } from "../constants";

/**
 * PositionManager - Utility functions for position calculations
 * 
 * LIQUIDATION PRICE:
 * - Use backend's liquidation price from Position objects or OrderPreview API
 * - Backend calculation includes cumulative funding payments (every 8 hours)
 * - Frontend calculation is deprecated (see method below)
 */
export class PositionManager {
  /**
   * @deprecated Use backend's liquidation price from Position or OrderPreview API
   * 
   * This frontend calculation omits cumulative funding payments which can cause
   * $100s of error for positions held over time (>8 hours). Funding payments
   * are paid/received every 8 hours and directly affect available margin.
   * 
   * Industry standard (Binance, dYdX, Bybit): Backend is source of truth for
   * all risk metrics including liquidation price.
   * 
   * Use instead:
   * - For existing positions: position.liquidationPrice (from backend)
   * - For order preview: OrdersApi.previewOrder().estimated_liquidation_price
   * 
   * This method is kept only as a fallback for edge cases where backend is unavailable.
   */
  static calculateEstimatedLiquidationPrice(
    entryPrice: number,
    side: PositionSide,
    leverage: number,
  ): number {
    // Tiered maintenance margin (matches backend)
    const mmr = leverage < 20 ? 0.01 : leverage < 50 ? 0.0075 : 0.004;
    const liquidationPenaltyRate = 0.0005; // 0.05%
    const totalRate = mmr + liquidationPenaltyRate;
    const marginFraction = 1 / leverage;
    
    if (side === "long") {
      const numerator = entryPrice * (1 - marginFraction);
      const denominator = 1 - totalRate;
      return numerator / denominator;
    } else {
      const numerator = entryPrice * (1 + marginFraction);
      const denominator = 1 + totalRate;
      return numerator / denominator;
    }
  }

  /**
  /**
   * Calculate unrealized P&L for a position
   */
  static calculateUnrealizedPnl(
    position: Position,
    currentPrice: number
  ): number {
    const priceDiff =
      position.side === "long"
        ? currentPrice - position.entryPrice
        : position.entryPrice - currentPrice;

    return priceDiff * position.size;
  }

  /**
   * Calculate P&L percentage based on collateral/margin
   */
  static calculatePnlPercentage(position: Position, currentPrice: number): number {
    const unrealizedPnl = this.calculateUnrealizedPnl(position, currentPrice);
    
    // Use collateral if available, otherwise calculate from position value / leverage
    let basis = position.collateral;
    if (!basis || basis === 0) {
      // Fallback: calculate collateral from position value and leverage
      const positionValue = position.size * position.entryPrice;
      basis = positionValue / (position.leverage || 1);
    }
    
    // Avoid division by zero
    if (basis === 0) return 0;
    
    return (unrealizedPnl / basis) * 100;
  }

  /**
   * Calculate required collateral for a position
   */
  static calculateRequiredCollateral(
    size: number,
    entryPrice: number,
    leverage: number
  ): number {
    const positionValue = size * entryPrice;
    return positionValue / leverage;
  }

  /**
   * Calculate position size from collateral and leverage
   */
  static calculatePositionSize(
    collateral: number,
    entryPrice: number,
    leverage: number
  ): number {
    const positionValue = collateral * leverage;
    return positionValue / entryPrice;
  }

  /**
   * Check if a position should be liquidated
   */
  static shouldLiquidate(position: Position, currentPrice: number): boolean {
    if (position.side === "long") {
      return currentPrice <= position.liquidationPrice;
    } else {
      return currentPrice >= position.liquidationPrice;
    }
  }

  /**
   * Calculate margin ratio for a position
   */
  static calculateMarginRatio(position: Position, currentPrice: number): number {
    const positionValue = position.size * currentPrice;
    const unrealizedPnl = this.calculateUnrealizedPnl(position, currentPrice);
    const equity = position.collateral + unrealizedPnl;

    return (equity / positionValue) * 100;
  }

  /**
   * Calculate return on equity (ROE)
   */
  static calculateROE(position: Position, currentPrice: number): number {
    const unrealizedPnl = this.calculateUnrealizedPnl(position, currentPrice);
    return (unrealizedPnl / position.collateral) * 100;
  }

  /**
   * Validate position parameters
   */
  static validatePosition(
    _market: MarketSymbol,
    size: number,
    price: number,
    leverage: number,
    availableBalance: number
  ): { valid: boolean; error?: string } {
    if (size <= 0) {
      return { valid: false, error: "Position size must be positive" };
    }

    if (price <= 0) {
      return { valid: false, error: "Price must be positive" };
    }

    if (leverage < 1 || leverage > 10) {
      return { valid: false, error: "Leverage must be between 1 and 10" };
    }

    const requiredCollateral = this.calculateRequiredCollateral(size, price, leverage);
    if (requiredCollateral > availableBalance) {
      return { valid: false, error: "Insufficient collateral" };
    }

    return { valid: true };
  }
}

